import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SCHEMA_SCHOOLS } from './schemaSchools';
import { LandingAuthButton, LandingFooter, LandingHeader, SignedInNotice, useInquiryForm } from './LandingShell';
import './landing.css';

const OnboardingLanding = () => {
  const form = useInquiryForm(SCHEMA_SCHOOLS, { formId: 'inquiryForm', submitId: 'submitBtn' });

  useEffect(() => {
    document.title = 'Acuity Learn — Request access for your school or district';
    return () => {
      document.title = 'User Information System';
    };
  }, []);

  return (
    <div className="onboarding-page">
      <LandingHeader logoTo="/onboarding" />

      <main id="top">
        <section className="hero">
          <div className="wrap">
            <span className="eyebrow">Now accepting schools and districts</span>
            <h1>The student information system and curriculum platform, in one place.</h1>
            <p className="lede">
              Acuity Learn brings your student records, curriculum, and career pathways together under one roof —
              so teachers spend their time teaching, administrators can see what is actually happening, and families
              stay informed. Tell us about your school and we&apos;ll be in touch with next steps.
            </p>
            <div className="hero-actions">
              <a className="btn btn-primary" href="#inquiry">Request access</a>
              <LandingAuthButton />
            </div>
            <p className="hero-note">Takes about three minutes. We&apos;ll only use your details to follow up about getting your school set up.</p>
            <p className="switcher">
              <span>Enrolling your own child?</span>
              <Link to="/families">Private school and homeschool families start here →</Link>
            </p>
          </div>
        </section>

        <section className="capabilities" aria-label="What Acuity Learn provides">
          <div className="wrap">
            <div className="cap-grid">
              <div className="cap">
                <div className="dot" style={{ background: 'rgba(15,110,86,.1)' }}>
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#0F6E56" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/></svg>
                </div>
                <h3>Student information</h3>
                <p>Enrollment, rosters, attendance, grades, and official records — with the reporting your state requires.</p>
              </div>
              <div className="cap">
                <div className="dot" style={{ background: 'rgba(43,113,59,.1)' }}>
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#2B713B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
                </div>
                <h3>Curriculum and lessons</h3>
                <p>Build standards-aligned courses once, publish them across schools, and let teachers adapt them to their classrooms.</p>
              </div>
              <div className="cap">
                <div className="dot" style={{ background: 'rgba(128,90,18,.1)' }}>
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#805A12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                </div>
                <h3>Career and technical education</h3>
                <p>Run CTE pathways alongside core academics, with progress and credentials tracked on the same record.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="form-section" id="inquiry">
          <div className="wrap">
            <div className="form-card">
              <div className="form-head" ref={form.formHeadRef}>
                <h2>Request access for your school or district</h2>
                <p>
                  Share a few details about your school and our team will review your request and follow up with next
                  steps and sign-in details once you&apos;re approved. Fields marked with an asterisk are required — the rest
                  are optional, so send what you know.
                </p>
                <SignedInNotice />
              </div>

              <div className="form-error" ref={form.errorRef} role="alert" />

              <form className="form-body" id={form.formId} ref={form.formRef} noValidate />

              <div className="form-foot" ref={form.formFootRef}>
                <p>We use your details only to follow up about setting up your school. We never ask for student information on this form.</p>
                <button className="btn btn-primary btn-submit" type="submit" form={form.formId} id={form.submitId} ref={form.submitRef}>
                  Submit request
                </button>
              </div>

              <div className="confirm" ref={form.confirmRef} role="status" aria-live="polite" tabIndex={-1}>
                <div className="mark">
                  <svg width="27" height="27" viewBox="0 0 24 24" fill="none" stroke="#2B713B" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <h2>Thank you — we&apos;ve got your request.</h2>
                <p ref={form.confirmNameRef}>Our team will review the details you shared and follow up by email with next steps.</p>
                <p>If your school is approved, that email will include sign-in details for your team.</p>
                <p className="ref" ref={form.confirmRefEl} />
              </div>
            </div>
          </div>
        </section>
      </main>

      <LandingFooter note="Acuity Learn is a K-12 student information and curriculum platform. Details submitted here are used only to follow up about school onboarding." />
    </div>
  );
};

export default OnboardingLanding;
